import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';

/**
 * #333 程序化纹理生成器 (Procedural Texture Generator)
 *
 * 来源：Creative coding effects - 程序化纹理
 * 类型：生成艺术 + Canvas 渲染
 * 技术要点：
 * - 程序化纹理算法（Voronoi、Perlin、Cellular）
 * - Canvas 2D 渲染（高性能）
 * - 颜色映射（HSL 渐变）
 * - 动态参数变化
 * - Spring 物理动画
 *
 * 实现难度：中高
 * 视觉效果：震撼、自然、生成艺术感
 * 适用场景：背景装饰、艺术展示、生成艺术、科技主题
 * Composition ID: ProceduralTextureGenerator
 */

// 简化的噪声函数（实际应使用 simplex-noise 库）
const noise2D = (x: number, y: number, seed: number = 0): number => {
  const n = Math.sin(x * 12.9898 + y * 78.233 + seed) * 43758.5453;
  return n - Math.floor(n);
};

// Voronoi 纹理生成
const generateVoronoiTexture = (
  width: number,
  height: number,
  cellCount: number,
  frame: number
): ImageData => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  const imageData = ctx.createImageData(width, height);

  // 生成种子点
  const seeds = Array.from({ length: cellCount }, (_, i) => ({
    x: (noise2D(i, 0, frame * 0.01) * width),
    y: (noise2D(i, 1, frame * 0.01) * height),
  }));

  // 计算每个像素到最近种子点的距离
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let minDist = Infinity;
      let secondMinDist = Infinity;

      for (const seed of seeds) {
        const dx = x - seed.x;
        const dy = y - seed.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < minDist) {
          secondMinDist = minDist;
          minDist = dist;
        } else if (dist < secondMinDist) {
          secondMinDist = dist;
        }
      }

      // 计算颜色（基于距离）
      const value = (secondMinDist - minDist) * 2;
      const hue = (frame * 2 + value * 10) % 360;
      const saturation = 70;
      const lightness = 30 + value * 20;

      // HSL 转 RGB
      const h = hue / 360;
      const s = saturation / 100;
      const l = lightness / 100;

      const hue2rgb = (p: number, q: number, t: number): number => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      const r = Math.round(hue2rgb(p, q, h + 1/3) * 255);
      const g = Math.round(hue2rgb(p, q, h) * 255);
      const b = Math.round(hue2rgb(p, q, h - 1/3) * 255);

      const idx = (y * width + x) * 4;
      imageData.data[idx] = r;
      imageData.data[idx + 1] = g;
      imageData.data[idx + 2] = b;
      imageData.data[idx + 3] = 255;
    }
  }

  return imageData;
};

export const ProceduralTextureGenerator: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  // Spring 进入动画
  const enterProgress = spring({
    frame,
    fps,
    config: { damping: 20, stiffness: 100 },
  });

  // 缩放动画
  const scale = interpolate(enterProgress, [0, 1], [1.5, 1]);

  // 旋转角度
  const rotation = interpolate(frame, [0, 150], [0, 10]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d')!;

    // 生成 Voronoi 纹理
    const imageData = generateVoronoiTexture(width, height, 30, frame);
    ctx.putImageData(imageData, 0, 0);
  }, [frame, width, height]);

  // 装饰元素
  const decorations = Array.from({ length: 5 }, (_, i) => {
    const angle = (i / 5) * Math.PI * 2 + frame * 0.02;
    const radius = 200 + Math.sin(frame * 0.1 + i) * 50;
    const x = width / 2 + Math.cos(angle) * radius;
    const y = height / 2 + Math.sin(angle) * radius;

    return { x, y, angle, radius };
  });

  return (
    <AbsoluteFill style={{ background: '#0a0a0a', overflow: 'hidden' }}>
      {/* Canvas 纹理 */}
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          position: 'absolute',
          transform: `scale(${scale}) rotate(${rotation}deg)`,
          opacity: enterProgress,
        }}
      />

      {/* 遮罩层（中心淡出） */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle, transparent 30%, rgba(10, 10, 10, 0.8) 70%)',
          opacity: 0.7,
        }}
      />

      {/* 装饰元素 */}
      {decorations.map((dec, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: dec.x,
            top: dec.y,
            width: 100,
            height: 100,
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '50%',
            transform: `translate(-50%, -50%) rotate(${dec.angle}rad)`,
            opacity: 0.3,
          }}
        />
      ))}

      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          opacity: enterProgress,
        }}
      >
        <div
          style={{
            fontSize: 56,
            fontWeight: 'bold',
            color: '#ffffff',
            marginBottom: 10,
            textShadow: '0 0 20px rgba(255, 255, 255, 0.5)',
          }}
        >
          Procedural Texture
        </div>
        <div
          style={{
            fontSize: 24,
            color: '#ffffff',
            opacity: 0.7,
          }}
        >
          Voronoi Diagram • 30 Cells
        </div>
      </div>
    </AbsoluteFill>
  );
};
